import { useState, useEffect, useCallback } from 'react';
import { ExecutionDocument } from '../../../shared/services/documentService';
import { documentCacheService } from '../../../shared/services/documentCacheService';
import { authenticateFromUrlToken } from '../../../core/lib/supabase';
import { configService } from '../../../shared/services/configService';

interface UseExecutionDocumentsResult {
  documents: ExecutionDocument[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  load: () => void; // Para lazy loading manual
}

/**
 * Hook to fetch knowledge base documents for the current execution and workpill.
 * Uses documentCacheService for optimized caching and lazy loading support.
 * 
 * @param executionId - Optional execution ID, falls back to configService
 * @param workpillId - Optional workpill ID, falls back to configService
 * @param lazy - If true, documents won't load automatically (default: false)
 * @returns Object with documents, loading state, error, and control functions
 */
export function useExecutionDocuments(
  executionId?: string,
  workpillId?: string,
  lazy = false
): UseExecutionDocumentsResult {
  const [documents, setDocuments] = useState<ExecutionDocument[]>([]);
  const [loading, setLoading] = useState(!lazy); // No cargar si es lazy
  const [error, setError] = useState<string | null>(null);
  const [shouldLoad, setShouldLoad] = useState(!lazy); // Control para lazy loading

  const fetchDocuments = useCallback(async (force = false) => {
    try {
      // Si es lazy y no se ha pedido cargar, salir
      if (!shouldLoad && !force) {
        console.log('⏸️ useExecutionDocuments: Lazy mode, esperando trigger manual');
        return;
      }

      setLoading(true);
      setError(null);
      
      // Intentar autenticación con tokens de URL primero
      await authenticateFromUrlToken();
      
      // Obtener configuración actual
      const config = configService.getConfig();
      const effectiveExecutionId = executionId || config.executionId;
      const effectiveWorkpillId = workpillId || config.workpillId;
      
      console.log('📚 useExecutionDocuments: Fetching documents...');
      console.log('📚   Execution ID:', effectiveExecutionId);
      console.log('📚   Workpill ID:', effectiveWorkpillId);
      console.log('📚   Lazy mode:', lazy);
      console.log('📚   Force refresh:', force);
      
      if (!effectiveExecutionId) {
        console.warn('⚠️ Missing execution ID');
        setDocuments([]);
        setLoading(false);
        return;
      }

      const knowledgeBases = await documentCacheService.getDocuments(
        effectiveExecutionId,
        effectiveWorkpillId ?? '',
        force
      );
      
      console.log(`📚 Received ${knowledgeBases.length} knowledge bases from cache`);
      
      // Transformar de KnowledgeBaseWithDocuments[] a ExecutionDocument[]
      const transformedDocuments: ExecutionDocument[] = knowledgeBases.flatMap(kb => 
        kb.documents.map(doc => ({
          id: doc.id,
          name: doc.name,
          type: 'document' as const,
          category: doc.parent_name,
          knowledge_base_name: kb.name,
          source_path: doc.storage_path,
          created_at: doc.created_at,
          status: doc.status,
          metadata: {
            knowledge_base_id: kb.id,
            parent_id: doc.parent_id
          }
        }))
      );
      
      console.log(`📚 Transformed to ${transformedDocuments.length} documents`);
      setDocuments(transformedDocuments);
    } catch (err) {
      console.error('❌ Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [executionId, workpillId, shouldLoad, lazy]);

  // Auto-cargar cuando shouldLoad cambia (o cuando IDs cambian si no es lazy)
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Función para trigger manual de carga (útil en modo lazy)
  const triggerLoad = useCallback(() => {
    console.log('🔄 Manual load triggered');
    setShouldLoad(true);
  }, []);

  // Función para refetch con force refresh
  const refetch = useCallback(() => {
    console.log('🔄 Force refetch triggered');
    fetchDocuments(true);
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refetch,
    load: triggerLoad
  };
}



